import { Box, Button, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";

import { Section, Seo } from "../marketing/MarketingLayout";

export const MODULES = [
  { key: "quoting", title: "Quoting & estimating", blurb: "Win more of the right work.", bullets: ["RFQ intake and assignment", "Multi-level BOM and routing cost rollups", "Quantity breaks, margin rules and approvals", "One-click quote-to-order"] },
  { key: "planning", title: "Planning & scheduling", blurb: "See the plant's next four weeks.", bullets: ["MRP with pegging and planner exceptions", "Finite and infinite scheduling", "Drag-and-drop planning board", "Skills, tooling and alternate machines"] },
  { key: "production", title: "Shop floor execution", blurb: "Every operation, live.", bullets: ["Work orders with frozen BOM/routing", "Barcode kiosk for labor and machine time", "Scrap, rework and holds", "Travelers, labels and OEE inputs"] },
  { key: "inventory", title: "Inventory & warehouse", blurb: "Trust your on-hand.", bullets: ["Lots, serials, bins and statuses", "Receiving, picking, kitting and cycle counts", "Genealogy forward and backward", "Consignment and customer-owned stock"] },
  { key: "quality", title: "Quality management", blurb: "Built in, not bolted on.", bullets: ["Receiving, in-process and final inspections", "NCR, disposition and CAPA", "Calibration and certificates of conformance", "Traceability reports for audits"] },
  { key: "finance", title: "Finance & costing", blurb: "Numbers your controller trusts.", bullets: ["Job costing, WIP and variances", "AR/AP, cash and GL", "Landed cost and multi-currency", "Month-end close checklist"] },
  { key: "shipping", title: "Shipping & logistics", blurb: "Out the door, on time.", bullets: ["Pick waves and packing", "Carrier labels and tracking", "Packing slips, BOL and ASNs", "Partial and drop shipments"] },
];
export const INDUSTRIES = [
  { key: "machine-shops", title: "Machine shops", blurb: "CNC job shops quoting hundreds of parts a month.", points: ["Fast estimating from routings", "Finite scheduling by machine", "Serial and lot traceability"] },
  { key: "fabricators", title: "Metal fabricators", blurb: "Cutting, forming, welding and finishing under one roof.", points: ["Nesting jobs and remnants", "Outside processing POs", "Weld and paint routing steps"] },
  { key: "contract-manufacturers", title: "Contract manufacturers", blurb: "Build-to-print with customer-supplied material.", points: ["Customer-owned inventory", "Customer requirements per order", "EDI 850/856/810"] },
  { key: "oem", title: "Equipment OEMs", blurb: "Configured products with installed-base service.", points: ["Product configurator", "As-built records", "Field service and warranty"] },
];

export function FeaturesPage() {
  return (
    <>
      <Seo title="Product" description="Quoting, planning, shop floor, inventory, quality, finance and shipping modules for manufacturers." />
      <Section>
        <Typography variant="h1" sx={{ fontSize: "2.4rem", mb: 1 }}>One system for the whole plant</Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>Pick the modules you need today; turn on the rest when you're ready.</Typography>
        <Stack spacing={6}>
          {MODULES.map((m, i) => (
            <Grid key={m.key} id={m.key} container spacing={4} direction={i % 2 ? "row-reverse" : "row"} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h3" sx={{ fontSize: "1.6rem" }}>{m.title}</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>{m.blurb}</Typography>
                <ul style={{ paddingLeft: 20 }}>{m.bullets.map((b) => <li key={b}><Typography variant="body2">{b}</Typography></li>)}</ul>
                <Button component={RouterLink} to="/signup" variant="outlined" size="small">Try it free</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ height: 220, borderRadius: 2, border: 1, borderColor: "divider", bgcolor: "background.paper", display: "grid", placeItems: "center", color: "text.secondary" }}>Screenshot: {m.title}</Box>
              </Grid>
            </Grid>
          ))}
        </Stack>
      </Section>
    </>
  );
}

export function IndustriesPage() {
  const { slug } = useParams();
  const selected = INDUSTRIES.find((i) => i.key === slug);
  return (
    <>
      <Seo title={selected ? selected.title : "Industries"} description="CloudForge ERM for machine shops, fabricators, contract manufacturers and equipment OEMs." />
      <Section>
        <Typography variant="h1" sx={{ fontSize: "2.4rem", mb: 1 }}>{selected ? selected.title : "Built for how you manufacture"}</Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>{selected ? selected.blurb : "Same platform, tuned to your process."}</Typography>
        <Grid container spacing={2}>
          {(selected ? [selected] : INDUSTRIES).map((ind) => (
            <Grid key={ind.key} size={{ xs: 12, md: selected ? 12 : 6 }}>
              <Card sx={{ height: "100%" }}><CardContent>
                <Typography variant="h5" gutterBottom>{ind.title}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>{ind.blurb}</Typography>
                <ul style={{ paddingLeft: 20 }}>{ind.points.map((p) => <li key={p}><Typography variant="body2">{p}</Typography></li>)}</ul>
                <Stack direction="row" spacing={1}>
                  {!selected && <Button component={RouterLink} to={`/industries/${ind.key}`} size="small">Learn more</Button>}
                  <Button component={RouterLink} to="/demo" size="small" variant="contained">Book a demo</Button>
                </Stack>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      </Section>
    </>
  );
}
